import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../../utils/apiErrors';
import type { ReportExportFormat } from './types';

/**
 * Runs one export at a time and tells the user where it got to.
 *
 * An export is a long request that produces no visible change on the page, so without the toasts
 * the user clicks again, and a second click on a running export is the fastest way to two identical
 * downloads and a doubled server-side render. `busy` is mirrored into a ref so the guard reads the
 * CURRENT value rather than the one captured when the handler was created.
 */

export interface UseReportExportResult {
  busy: ReportExportFormat | null;
  /**
   * @param format   which button the user pressed, used for the busy state
   * @param run      produces the file; typically `postReportExport(url, request)`
   * @param filename the name the browser saves it under, extension included
   */
  exportReport: (
    format: ReportExportFormat,
    run: () => Promise<ArrayBuffer | Blob>,
    filename: string,
  ) => Promise<void>;
}

function saveBlob(data: ArrayBuffer | Blob, filename: string) {
  const blob = data instanceof Blob ? data : new Blob([data]);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  // Revoking synchronously can cancel the download in some browsers, so let the click settle first.
  setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
  }, 0);
}

export function useReportExport(): UseReportExportResult {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<ReportExportFormat | null>(null);
  const busyRef = useRef<ReportExportFormat | null>(null);

  const exportReport = useCallback(
    async (
      format: ReportExportFormat,
      run: () => Promise<ArrayBuffer | Blob>,
      filename: string,
    ) => {
      if (busyRef.current) return;
      busyRef.current = format;
      setBusy(format);
      const toastId = toast.loading(t('reports.exportStarted'));
      try {
        const data = await run();
        saveBlob(data, filename);
        toast.success(t('reports.exportReady', { filename }), { id: toastId });
      } catch (error) {
        toast.error(getApiErrorMessage(error, t, t('reports.exportFailed')), { id: toastId });
      } finally {
        busyRef.current = null;
        setBusy(null);
      }
    },
    [t],
  );

  return { busy, exportReport };
}

export default useReportExport;
